import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Redirect } from "react-router-dom";
import {
  SortByType,
  SortOrderType,
  UserAuthorityType,
  UserResponse,
  UserRoleType,
} from "../../generated/user-api";
import { hasPermission } from "../../Components/PermissionController/PermissionController";
import UserList from "../../Components/UserList/UserList";
import {
  useCustomerFeatures,
  useCustomerId,
} from "../../Providers/CustomerProvider/CustomerProvider";
import { useCustomerCurrentUpdate } from "../../Providers/RecentUpdatesProvider/RecentUpdatesProvider";
import CustomPagination from "../../Components/CustomPagination/Pagination";
import SearchBar from "../../Components/SearchBar/SearchBar";
import { messages } from "./AdminExternals.messages";
import { useStyles } from "./AdminExternals.style";
import { useAuthenticatedUser } from "../../Providers/AuthenticatedUserProvider/AuthenticatedUserProvider";
import CreateUserButton from "../../Components/Buttons/CreateUserButton/CreateUserButton";
import { useSpinner } from "../../Components/Spinner/Spinner";
import { RoutePath } from "../../Components/Routes/RoutePath";
import { userService } from "../../Providers/ServiceProvider/ServiceProvider";
import { PaginatedUserList } from "../../Models/User";
import InviteUserButton from "../../Components/Buttons/InviteUserButton/InviteUserButton";
import { hasFeatureEnabled } from "../../Components/FeatureController/FeatureController";
import { CustomerFeatureType } from "../../generated/customersettings-api";
import { Variant } from "../../Components/UserList/Components/Variant/Variant";

const AdminExternals = () => {
  const intl = useIntl();
  const customerId = useCustomerId();
  const [authenticatedUser] = useAuthenticatedUser();
  const customerFeatures = useCustomerFeatures();
  const classes = useStyles();
  const [result, setResult] = useState<PaginatedUserList>();
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [query, setQuery] = useState<string>();
  const setSpinner = useSpinner()[1];
  const [isUserListLoading, setIsUserListLoading] = useState<boolean>(false);
  const [customerupdates] = useCustomerCurrentUpdate();

  const isEmailQuery = () => {
    return query?.includes("@");
  };
  const isLoading = (): boolean => {
    return isUserListLoading;
  };

  const loadUserList = () => {
    if (customerId) {
      setIsUserListLoading(true);
      userService()
        .listUsersByCustomerAndSearchParams({
          name: query !== "" && !isEmailQuery() ? query : undefined,
          email: query !== "" && isEmailQuery() ? query : undefined,
          pageSize,
          page,
          customerId,
          userRole: UserRoleType.External,
          sortBy: SortByType.LastName,
          sortOrder: SortOrderType.Ascending,
        })
        .then((res) => setResult(res))
        .finally(() => setIsUserListLoading(false));
    }
  };

  const hasAdminRoutePermission = hasPermission({
    currentUser: authenticatedUser.user,
    requiredAuthoritiesStaff: [UserAuthorityType.AdminSite],
  });

  const hasAdminRouteFeaturePermission = hasFeatureEnabled(
    CustomerFeatureType.ExternalUsers,
    customerFeatures
  );

  useEffect(() => {
    loadUserList();
  }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page !== 1) {
      // Reset pagination when search is triggered
      setPage(1);
    } else {
      loadUserList();
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSpinner({ isLoading: isLoading() });
  }, [isLoading()]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasAdminRoutePermission || !hasAdminRouteFeaturePermission) {
    return <Redirect exact to={RoutePath.DASHBOARD || ""} />;
  }

  return (
    <>
      <div className={classes.actionContainer}>
        {customerId && (
          <CreateUserButton
            onCreated={loadUserList}
            userRole={UserRoleType.External}
            label={intl.formatMessage(messages.createButton)}
            dialogTitle={intl.formatMessage(messages.createDialogTitle)}
            customerId={customerId}
          />
        )}
        <InviteUserButton
          onCreated={loadUserList}
          userRole={UserRoleType.External}
          label={intl.formatMessage(messages.inviteButton)}
          dialogTitle={intl.formatMessage(messages.inviteDialogTitle)}
        />
      </div>
      {customerupdates &&
        customerupdates.userResponse.filter((element: UserResponse) => {
          return element.userRole === UserRoleType.External;
        })?.length > 0 && (
          <div className={classes.newlyAddedContainer}>
            <UserList
              variant={Variant.ADMIN_NEWLY_ADDED}
              userList={customerupdates.userResponse.filter(
                (element: UserResponse) => {
                  return element.userRole === UserRoleType.External;
                }
              )}
              onUpdated={loadUserList}
            />
          </div>
        )}
      <SearchBar
        placeholder={intl.formatMessage(messages.searchbarPlaceholder)}
        onSubmit={(newQuery) => setQuery(newQuery)}
      />
      {!isLoading() && (
        <>
          <UserList userList={result?.data} onUpdated={loadUserList} />
          <CustomPagination
            onChange={setPage}
            pagination={result?.pagination}
          />
        </>
      )}
    </>
  );
};

export default AdminExternals;
